import React from "react";
import Link from "next/link";
import { useLocale, useTranslations } from 'next-intl';
import { useServicesData } from '../ServicesDataContext';
import { 
  MessageSquare, 
  Network, 
  HardDrive, 
  ClipboardCheck, 
  Zap, 
  Code, 
  Brain, 
  Shield,
  Users
} from 'lucide-react';

// Icon mapping for each service
const getServiceIcon = (serviceId: string) => {
  const iconMap = {
    itConsult: MessageSquare,      // IT Consultation - communication/advice
    networkSetup: Network,         // Network Setup - network infrastructure
    dataBackup: HardDrive,         // Data Backup - storage/backup
    itAudit: ClipboardCheck,       // IT Audit - auditing/checking
    techSolutions: Zap,            // Tech Solutions Pro - power/efficiency
    codeCrafters: Code,            // Code Crafters - programming
    byteGenius: Brain,             // Byte Genius - AI/intelligence
    netNinjas: Shield,             // Net Ninjas - security/protection
    teamAugmentation: Users,       // Team Augmentation - people/team
  };
  
  return iconMap[serviceId as keyof typeof iconMap] || MessageSquare;
};

export const ServicesCardsSection: React.FC = () => {
  const t = useTranslations('Services');
  const locale = useLocale();
  const { items } = useServicesData();
  
  // Map service IDs to their routes
  const getServiceRoute = (serviceId: string): string => {
    if (serviceId === 'teamAugmentation') {
      return `/${locale}/team-augmentation`;
    }
    // Other services don't have dedicated pages yet
    return '#';
  };
  
  // Handle navigation with loading
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('navigation-start', {
        detail: { href: e.currentTarget.href }
      }));
    }
  };
  
  return (
    <section className="relative w-full py-16 md:py-20 lg:py-24 bg-white">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex flex-wrap justify-center gap-6 md:gap-8">
          {items.map((item, index) => {
            const IconComponent = getServiceIcon(item.id);
            const route = getServiceRoute(item.id);
            const isClickable = route !== '#';
            
            const CardContent = (
              <div className={`flex flex-col items-center justify-center text-center w-[300px] h-[344px] rounded-xl bg-white border border-gray-200 shadow-[0_2px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(53,30,224,0.15)] transition-all duration-300 hover:-translate-y-2 p-6 md:p-8 ${isClickable ? 'cursor-pointer' : ''}`}>
                {/* Icon */}
                <div className="relative w-20 h-20 md:w-32 md:h-32 mb-6">
                  {/* Gradient border (visible on hover) */}
                  <div className="absolute inset-0 rounded-full bg-brand-gradient opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Icon background */}
                  <div className="absolute inset-[2px] flex items-center justify-center w-[calc(100%-4px)] h-[calc(100%-4px)] rounded-full bg-[#F9F9F9] group-hover:bg-brand-gradient transition-all duration-300">
                    <IconComponent 
                      size={40} 
                      className="text-[#05092B] group-hover:text-white stroke-2 transition-colors duration-300" 
                    />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-gray-900 text-lg md:text-xl font-semibold tracking-tight mb-2">
                  {t(`cards.${item.id}.title`, { defaultMessage: item.defaultTitle || "" })}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-6">
                  {t(`cards.${item.id}.description`, { defaultMessage: item.defaultDescription || "" })}
                </p>
              </div>
            );
            
            return (
              <div key={item.id} className="relative group">
                {isClickable ? (
                  <Link href={route} onClick={handleNavigation} className="block">
                    {CardContent}
                  </Link>
                ) : (
                  CardContent
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesCardsSection;


